import { Component, Input, Output, EventEmitter, signal } from '@angular/core';
import { forkJoin, from, of } from 'rxjs';
import { DialogComponent } from 'src/app/shared/components/dialog/dialog.component';
import { IDialogSettings } from 'src/app/shared/interfaces/dialog.interface';
import { GpsConnectivityService } from 'src/app/shared/services/gps-connectivity.service';
import { DeadzoneRideDetail } from 'src/app/shared/interfaces/deadzones-ride-detail.interface';
import { cellToLatLng, getHexagonEdgeLengthAvg } from 'h3-js';
import { BasicTableComponent } from 'src/app/shared/components/basic-table/basic-table.component';
import { gpsSignalFromAccuracies } from '../utils/gps-strength.util';
export interface DeadzoneDetails {
  h3Id?: string;
  state?: string;
  location?: string;
  averageStrength?: number;
  totalIncidents?: number;
  period?: string | { start: string; end: string };
  radiusMiles?: number;
}
export interface SelectedZone {
  id?: string | number; // H3 zone ID
  entityId?: number; // Backend entity ID for API calls
  createdAtUtc?: Date; // Zone creation timestamp
  connectionStatusId?: number; // Connection status from backend
  name?: string;
  paths?: google.maps.LatLngLiteral[] | google.maps.LatLngLiteral[][];
  meta?: any;
}

@Component({
  selector: 'app-deadzones-details-dialog',
  imports: [DialogComponent, BasicTableComponent],
  templateUrl: './deadzones-details-dialog.component.html',
  styleUrls: ['./deadzones-details-dialog.component.scss'],
})
export class DeadzonesDetailsDialogComponent {
  @Input() visible = false;
  @Input() header = 'Deadzone Details';
  @Input() zone?: SelectedZone | null;
  @Output() visibleChange = new EventEmitter<boolean>();

  public loading = false;
  public rows = signal<DeadzoneRideDetail[]>([]);
  public details: DeadzoneDetails | null = null;
  public showIndex = true;

  // Basic-table input builder
  public tableData() {
    const data = this.rows().map((r) => ({
      rideId: r.rideId,
      driverName: r.driverName,
      timestamp: this.formatTimestamp(r.timestamp),
      duration: `${this.formatDuration(r.duration)}`,
    }));

    return {
      columns: [
        { name: 'rideId', displayName: 'RIDE ID' },
        { name: 'driverName', displayName: 'DRIVER' },
        { name: 'timestamp', displayName: 'AFFECTED AT' },
        { name: 'duration', displayName: 'DURATION' },
      ],
      data,
    };
  }

  public settings: IDialogSettings = {
    showCustomHeader: false,
    modal: true,
    position: 'center',
    isMaximizable: false,
    breakpoints: { '960px': '75vw', '640px': '90vw' },
    isDraggable: false,
    isResizable: true,
    closeOnEscape: true,
    showCustomFooter: false,
    width: '1000px',
    maxWidth: '1000px',
  };

  constructor(private readonly gps: GpsConnectivityService) {}

  // Called when dialog opens (hook via app-dialog onShow)
  public handleDialogShow(): void {
    const entityId = this.zone?.entityId;
    const h3Id = this.zone?.id;

    if (!entityId) {
      console.error('No entity ID provided for deadzone dialog');
      return;
    }

    this.loading = true;

    const geocode$ =
      h3Id &&
      typeof h3Id === 'string' &&
      typeof google !== 'undefined' &&
      google.maps
        ? (() => {
            const [lat, lng] = cellToLatLng(h3Id);
            return from(this.reverseGeocode(lat, lng));
          })()
        : of({ state: null, location: null });

    forkJoin({
      rideDetails: this.gps.getDeadzoneRideDetails(entityId),
      geocode: geocode$,
    }).subscribe({
      next: ({ rideDetails, geocode }) => {
        // Calculate total incidents from array length
        const totalIncidents = rideDetails.length;

        // Calculate period: find min and max of affectedAtUtc
        let minDate: Date | null = null;
        let maxDate: Date | null = null;

        rideDetails.forEach((ride) => {
          const affectedDate = new Date(ride.affectedAtUtc);
          if (!minDate || affectedDate < minDate) {
            minDate = affectedDate;
          }
          if (!maxDate || affectedDate > maxDate) {
            maxDate = affectedDate;
          }
        });

        // Calculate radius from H3 hexagon
        let radiusMiles = 0;
        if (h3Id && typeof h3Id === 'string') {
          try {
            // Get H3 resolution (0-15)
            const resolution = Number.parseInt(h3Id.charAt(1), 10);
            // Get average edge length in kilometers
            const edgeLengthKm = getHexagonEdgeLengthAvg(resolution, 'km');
            // Convert to approximate radius in miles (hexagon inscribed circle radius ≈ edge * 0.866)
            radiusMiles = Number.parseFloat(
              (edgeLengthKm * 0.866 * 0.621371).toFixed(2),
            );
          } catch (err) {
            console.warn('Failed to calculate H3 radius:', err);
          }
        }

        // Use concurrent reverse geocode result
        const geocodeResult: { state: string | null; location: string | null } =
          geocode;

        // Build DeadzoneDetails object
        this.details = {
          h3Id: String(h3Id ?? ''),
          state: geocodeResult.state ?? 'Unknown',
          location: geocodeResult.location ?? 'Unknown',
          averageStrength: gpsSignalFromAccuracies(
            rideDetails.map((rd) => rd.averageAccuracy),
            {
              method: 'exp',
              acceptable: 2,
              good: 1,
            },
          ).score,
          totalIncidents: totalIncidents,
          period:
            minDate && maxDate
              ? {
                  start: minDate,
                  end: maxDate,
                }
              : '',
          radiusMiles: radiusMiles,
        };

        // Map backend response to table rows
        this.rows.set(
          rideDetails.map((ride: any) => ({
            rideId: ride.rideId,
            driverName: ride.driverName ?? 'Unknown',
            timestamp: ride.affectedAtUtc,
            duration: ride.durationTimeInSecond,
          })),
        );

        this.loading = false;
      },
      error: (err) => {
        console.error('Error fetching deadzone ride details:', err);
        this.rows.set([]);
        this.loading = false;
      },
    });
  }

  public formatPeriod(period: any): string {
    if (!period) return '';
    if (typeof period === 'string') return period;
    const start = period.start ? new Date(period.start) : null;
    const end = period.end ? new Date(period.end) : null;
    const fmt = (d: Date | null) =>
      d
        ? `${d.toLocaleDateString(undefined, { month: 'short', day: '2-digit', year: 'numeric' })}`
        : '';
    return `${fmt(start)}${start && end ? ' – ' : ''}${fmt(end)}`;
  }

  private formatTimestamp(value: string | Date): string {
    const d = typeof value === 'string' ? new Date(value) : value;
    const mm = String(d.getMonth() + 1).padStart(2, '0');
    const dd = String(d.getDate()).padStart(2, '0');
    const yyyy = d.getFullYear();
    let hours = d.getHours();
    const minutes = String(d.getMinutes()).padStart(2, '0');
    const ampm = hours >= 12 ? 'PM' : 'AM';
    hours = hours % 12;
    if (hours === 0) hours = 12;
    const hh = String(hours).padStart(2, '0');
    return `${mm}/${dd}/${yyyy} ${hh}:${minutes} ${ampm}`;
  }

  private formatDuration(totalSeconds: number | undefined | null): string {
    const secs = Math.max(0, Number(totalSeconds) || 0);
    const minutes = Math.floor(secs / 60);
    const seconds = secs % 60;
    if (minutes === 0) {
      return `${seconds} seconds`;
    }
    if (seconds === 0) {
      return `${minutes} minutes`;
    }
    console.log('Formatted duration:', minutes, seconds);
    return `${minutes} minutes ${seconds} seconds`;
  }

  // Called when dialog closes (hook via app-dialog onHide)
  public handleDialogHide(): void {
    // Reset state when dialog closes
    this.loading = false;
    this.details = null;
    this.rows.set([]);
    // Emit visible change to parent
    this.visibleChange.emit(false);
  }

  public reverseGeocode(
    lat: number,
    lng: number,
  ): Promise<{
    state: string | null;
    location: string | null;
  }> {
    const geocoder = new google.maps.Geocoder();
    return new Promise((resolve) => {
      const request = { location: { lat, lng } } as google.maps.GeocoderRequest;

      const handleGeocode = (
        results: google.maps.GeocoderResult[] | null,
        status: string,
      ) => {
        if (status !== google.maps.GeocoderStatus.OK || !results?.[0]) {
          console.warn('Geocode failed:', status);
          resolve({ state: null, location: null });
          return;
        }

        const { state, location } = this.extractStateAndLocation(
          results[0].address_components ?? [],
        );
        resolve({ state, location });
      };

      geocoder.geocode(request, handleGeocode);
    });
  }

  private extractStateAndLocation(
    components: google.maps.GeocoderAddressComponent[],
  ): { state: string | null; location: string | null } {
    const find = (type: string) =>
      components.find((c) => c.types?.includes(type)) || null;

    const stateComp = find('administrative_area_level_1');
    const countyComp = find('administrative_area_level_2');
    const locality = find('locality');
    const sublocality = find('sublocality');
    const neighborhood = find('neighborhood');

    const locationComp =
      countyComp || locality || sublocality || neighborhood || null;

    return {
      state: stateComp ? stateComp.long_name : null,
      location: locationComp ? locationComp.long_name : null,
    };
  }
}
